import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { getSessionId, getStoredUTM, getUserId, setUserId } from "../lib/utm";
import { getInviteSlug, getInviteTag } from "../lib/invite";
import MobileCaptureDialog from "../components/MobileCaptureDialog";
import { toast } from "sonner";
import { Coins, Loader2 } from "lucide-react";

/**
 * Recipient landing for a shared referral link.
 *   URL shape:  /apply/<productId>?ref=<sharer_hitch_user_id>
 *
 * Fast-load rules (2026-08-07 user report — page was blank/slow):
 *  - Open the mobile capture dialog IMMEDIATELY on mount (before any API
 *    call).  The dialog is what the user came to see.
 *  - Fetch product via the direct GET /products/:id endpoint (was fetching
 *    the whole product list and filtering — slow on prod).
 *  - Brand name hydrates onto the modal once the product resolves.
 *  - Redirect uses `window.location.href` (same tab, no popup).
 */
export default function SharedApplyLanding() {
  const { productId } = useParams();
  const [search] = useSearchParams();
  const sharerId = search.get("ref") || null;

  const [product, setProduct] = useState(null);
  const productRef = useRef(null); // stable ref for callback closures
  const [notFound, setNotFound] = useState(false);
  // Show dialog IMMEDIATELY — do not wait for the product fetch.
  const [showMobile, setShowMobile] = useState(true);
  const [status, setStatus] = useState("loading"); // loading | ready | redirecting
  const pendingRef = useRef(false); // mobile submitted before product resolved

  useEffect(() => {
    // Fast path: fetch just this product, not the full list.
    api
      .get(`/products/${productId}`)
      .then((r) => {
        const p = r?.data;
        if (!p) { setNotFound(true); return; }
        setProduct(p);
        productRef.current = p;
        setStatus("ready");
        // If the user already submitted their phone before this resolved,
        // fire the redirect now.
        if (pendingRef.current) {
          pendingRef.current = false;
          fireClick(p);
          return;
        }
        // Returning visitor short-circuit: skip dialog and redirect straight away.
        if (getUserId()) {
          setShowMobile(false);
          fireClick(p);
        }
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          toast.error("Could not load this offer. Please try again.");
          setStatus("ready");
        }
      });
  }, [productId]);

  const fireClick = async (p) => {
    setStatus("redirecting");
    try {
      const inviteSlug = getInviteSlug();
      const inviteTag = getInviteTag();
      const { data } = await api.post("/track/click", {
        product_id: p.id,
        session_id: getSessionId(),
        user_id: getUserId() || undefined,
        sharer_id: sharerId || undefined,
        ...getStoredUTM(),
        referrer: typeof document !== "undefined" ? document.referrer : "",
        current_url: typeof window !== "undefined" ? window.location.href : "",
        ...(inviteSlug ? { invite_slug: inviteSlug } : {}),
        ...(inviteSlug && inviteTag ? { invite_tag: inviteTag } : {}),
      });
      if (data?.user_id) setUserId(data.user_id);
      const url = data?.tracked_redirect_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Could not build partner link. Please try again.");
        setStatus("ready");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
      setStatus("ready");
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-black text-white px-6">
        <div className="text-center max-w-md">
          <div className="font-display text-2xl mb-2">This offer is no longer available</div>
          <p className="text-sm text-white/60">
            Browse other partners on{" "}
            <a href="/" className="underline">Offer Express</a>.
          </p>
        </div>
      </div>
    );
  }

  const brand = product?.provider || product?.title || "the partner";
  const isBanking = product?.type === "banking";

  return (
    <div className="min-h-screen grid place-items-center bg-black text-white">
      <div className="text-center px-6">
        <div className="inline-flex items-center gap-2 mb-4 opacity-80">
          <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 grid place-items-center">
            <Coins className="w-4 h-4" />
          </div>
          <div className="text-left">
            <div className="font-display font-bold text-sm leading-none">offer express</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-white/50 mt-0.5">referral link</div>
          </div>
        </div>
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-white/50" />
        <p className="mt-3 text-xs text-white/50">
          {status === "redirecting"
            ? `Redirecting you to ${brand}…`
            : status === "loading"
            ? "Loading your offer…"
            : "Enter your mobile in the popup to continue."}
        </p>
      </div>

      <MobileCaptureDialog
        open={showMobile}
        onOpenChange={(v) => {
          // Blocking: only allow close once we've started the redirect.
          if (!v && status !== "redirecting") return;
          setShowMobile(v);
        }}
        product={product}
        brand={brand}
        badge={product && !isBanking ? "Cashback tracking" : undefined}
        title={
          product && !isBanking
            ? "Enter your mobile to unlock cashback"
            : undefined // banking default: "Enter your mobile to continue"
        }
        description={
          product && !isBanking
            ? `Save ${brand} to your Offer Express account so we can track and pay out your cashback.`
            : undefined
        }
        ctaLabel={
          product && !isBanking
            ? `Continue to ${brand}`
            : undefined
        }
        onCaptured={async () => {
          setShowMobile(false);
          if (productRef.current) {
            await fireClick(productRef.current);
          } else {
            // Product still loading — flag so the fetch handler fires the
            // redirect when it resolves.
            pendingRef.current = true;
            setStatus("redirecting");
          }
        }}
      />
    </div>
  );
}
